<template>
  <div>
    <v-sheet elevation="1">
      <v-form ref="form">
        <v-toolbar dense color="white">
          <v-toolbar-title>{{ screenTitle }}</v-toolbar-title>
        </v-toolbar>
        <v-container fluid>
          <error-message :error-object="errorObject" />
          <success-message :success-object="successObject" />
          <p style="margin: 5px 0 15px 0">
            OILは下の表を参照してください。条件を指定することで一覧から絞り込むことができます。
          </p>
          <v-row>
            <v-col cols="6" md="3">
              <v-select
                v-model="searchItem.disaster"
                :items="disasters"
                :placeholder="config.PLACEHOLDER_SELECT"
                label="災害名称"
                item-text="disaster_name"
                item-value="disaster_no"
                return-object
                clearable
                outlined
                @change="reload"
              >
              </v-select>
            </v-col>
            <v-col cols="6" md="3">
              <v-select
                v-model="searchItem.level"
                :disabled="!hasDisaster(searchItem)"
                :items="levels"
                :placeholder="config.PLACEHOLDER_SELECT"
                label="OILレベル"
                clearable
                outlined
              ></v-select>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <top-operation-data-table
        :headers="headers"
        :items="items"
        :sort-by="['municip_name']"
        ref="oilDataTable"
        locale="ja-jp"
        v-if="hasDisaster(searchItem)"
        must-sort
      >
        <template v-slot:[`item.oil_lev`]="{ item }">
          <v-select
            v-model="item.oil_lev"
            :items="levels"
            :disabled="
              listFlag || !geClassFlag || !hasUpdateAuthorityByItem(item)
            "
            :placeholder="config.PLACEHOLDER_SELECT"
            @change="changeOillev(item.municip_code)"
            dense
          >
          </v-select>
        </template>
      </top-operation-data-table>
      <v-row v-if="!listFlag">
        <v-col cols="2">
          <v-btn
            color="primary"
            v-if="items.length > 0"
            :disabled="!geClassFlag"
            @click="save"
            >OILレベル一括更新</v-btn
          >
        </v-col>
      </v-row>
      <confirm-dialog ref="confirm" />
      <v-overlay :value="overlay" opacity="1" color="white">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-sheet>
  </div>
</template>

<script>
import Vue from "vue";
import Disaster from "@/plugins/disaster";
import messageUtils from "@/mixins/message-utils";
import config from "@/js/const";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import { DisasterTranslator } from "@/js/translators/disasterTranslator";
import { ScreenId } from "@/js/value-objects/screen-id";
import { ScreenCrud } from "@/js/value-objects/screen-crud";

const disasterInfoRepository = RepositoryFactory.get("disasterInfo");
const oilRepository = RepositoryFactory.get("oil");

Vue.use(Disaster);
export default {
  mixins: [messageUtils],
  components: {
    ConfirmDialog: () => import("@/components/ConfirmDialog"),
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    TopOperationDataTable: () => import("@/components/ui/TopOperationDataTable")
  },

  data: () => ({
    screenId: ScreenId.SF2001,
    overlay: false,
    listFlag: false,
    geClassFlag: false,
    config: config,
    searchItem: {
      disaster: "",
      level: ""
    },
    items: [],
    changeItems: [],
    disasters: []
  }),

  computed: {
    screenTitle() {
      if (this.listFlag) {
        return ScreenId.getScreenTitle(this.screenId) + "（参照）";
      } else {
        return ScreenId.getScreenTitle(this.screenId) + "（登録・編集・削除）";
      }
    },

    headers() {
      return [
        {
          text: "市町村",
          value: "municip_name"
        },
        {
          text: "OILレベル",
          value: "oil_lev",
          filter: this.levelFilter
        }
      ];
    },

    levels() {
      return [
        {
          text: "-",
          value: "0"
        },
        {
          text: "1",
          value: "1"
        },
        {
          text: "2",
          value: "2"
        },
        {
          text: "3",
          value: "3"
        },
        {
          text: "4",
          value: "4"
        },
        {
          text: "6",
          value: "6"
        },
        {
          text: "解除",
          value: "9"
        }
      ];
    },

    hasDisaster: function() {
      return function(searchItem) {
        return searchItem.disaster;
      };
    }
  },

  watch: {
    $route: "initialize"
  },

  mounted() {
    this.pageTitle(this.screenTitle);
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      this.overlay = true;
      const data = this.$screenAuthority.find(auth => {
        const screenCrud = ScreenCrud.factory(auth.screen_crud);
        return auth.screen_id === this.screenId && screenCrud.isOperation();
      });
      if (!data) {
        this.listFlag = true;
      }

      // 災害情報取得
      await disasterInfoRepository
        .list(this.screenId)
        .then(response => {
          this.disasters = DisasterTranslator.sortByDisasterNo(
            response.data.results,
            true
          );
        })
        .catch(e => {
          this.handleError(e, screen, "aggregation-menu");
        });

      // 検索項目に災害名称を設定
      this.searchItem.disaster = this.$disaster;
      // OIL情報取得
      await this.getOilInfo().catch(e => {
        this.handleError(e, screen, "aggregation-menu");
      });
      this.overlay = false;
    },

    async getOilInfo() {
      this.changeItems = [];
      // 検索項目に災害名称が設定されていれば実行
      if (this.searchItem.disaster && this.searchItem.disaster.disaster_no) {
        const payload = {
          disaster_no: this.searchItem.disaster.disaster_no
        };
        // OIL情報取得
        const items = await oilRepository.list(this.screenId, payload);
        this.items = items.data.results;
        // 緊急事態区分がGEの場合、フラグを変更
        this.geClassFlag =
          "GE" === this.searchItem.disaster.emerg_cls ? true : false;
      } else {
        this.items = [];
        this.geClassFlag = false;
      }
    },

    async save() {
      // OILレベルを変更していない場合は更新しない
      if (this.changeItems.length === 0) {
        return false;
      }
      // 更新確認ダイアログを表示
      const message = `OILレベルを一括で更新します。よろしいですか？`;
      const result = await this.$refs.confirm.open(message, "更新");
      if (!result) {
        return false;
      }
      this.overlay = true;
      this.clearMessage();
      // OILの更新を行う
      const oilList = this.items
        .filter(item => this.changeItems.includes(item.municip_code))
        .map(item => ({
          disaster_no: this.searchItem.disaster.disaster_no,
          municip_code: item.municip_code,
          oil_lev: item.oil_lev,
          hash: item.hash
        }));
      const payload = {
        list: oilList
      };
      await oilRepository
        .update(this.screenId, payload)
        .then(response => {
          this.handleSuccess(response);
        })
        .catch(error => {
          this.handleUpdateError(error);
        });
      // 登録後の最新データを取得する
      await this.getOilInfo().catch(error => {
        this.handleUpdateError(error);
      });
      this.overlay = false;
    },

    changeOillev(municipCode) {
      // 更新用にOILレベルを変更したデータを記録する
      if (!this.changeItems.includes(municipCode)) {
        this.changeItems.push(municipCode);
      }
    },

    async reload() {
      this.overlay = true;
      this.clearMessage();
      await this.getOilInfo().catch(error => {
        this.handleUpdateError(error);
      });
      this.overlay = false;
    },

    levelFilter(value) {
      this.resetPage();
      if (!this.searchItem.level || this.searchItem.level === "") {
        return true;
      }
      return value === this.searchItem.level;
    },

    // 1ページ目に戻す処理
    resetPage() {
      if (this.$refs.oilDataTable) {
        this.$refs.oilDataTable.resetPage();
      }
    }
  }
};
</script>
